// Value-preserving fallback widgets used when no host widgetComponents entry is
// registered for a passthrough markup widget. They never bundle the real widget
// behavior (chem editors, country/state datasets); they only keep the stored
// value intact and remain accessible.

import { FieldShell } from '../FieldShell'
import type { FieldComponentProps } from '../rendererContext'
import { controlClass, useControlMeta } from '../fields/useControlMeta'

function displayString(value: unknown): string {
  if (value == null) return ''
  return typeof value === 'string' ? value : JSON.stringify(value)
}

/** Editable text fallback (country / state) — writes a plain string to the store. */
export function TextPassthroughWidget({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  return (
    <FieldShell field={field} path={path} errors={errors}>
      <input
        id={meta.id}
        type="text"
        className={controlClass('form-control', meta.classNames.control, meta.hasErrors)}
        value={value == null ? '' : String(value)}
        disabled={meta.disabled}
        aria-invalid={meta.ariaInvalid}
        aria-describedby={meta.describedBy}
        onChange={(event) => meta.setFieldValue(path, event.target.value)}
        onBlur={() => meta.notifyBlur(path)}
      />
    </FieldShell>
  )
}

/**
 * Read-only fallback (image / chem_doodle / chem_writer) — these need a
 * host-provided editor, so we surface the raw stored value without mutating it,
 * keeping the value shape intact for round-tripping.
 */
export function ReadonlyPassthroughWidget({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const text = displayString(value)
  return (
    <FieldShell field={field} path={path} errors={errors}>
      {text ? (
        <pre id={meta.id} className="form-control-plaintext mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
          {text}
        </pre>
      ) : (
        <div id={meta.id} className="form-text fst-italic">
          No value (widget not available in this host).
        </div>
      )}
    </FieldShell>
  )
}
